export const phaseTwoKinds = new Set([
  "health_bp",
  "health_weight",
  "health_sugar",
  "health_hr",
  "health_personal",
  "study_task",
  "study_quiz",
  "study_project",
  "study_exam",
  "review",
  "water",
]);
export const recordKindLabels: Record<string, string> = {
  note: "笔记",
  meal: "饮食",
  exercise: "运动",
  health: "健康测量",
  study: "学习日志",
  review: "目标复盘",
  water: "每日饮水",
};
export const recordSubtypes: Record<string, string[][]> = {
  health: [
    ["bp", "血压"],
    ["weight", "体重"],
    ["sugar", "血糖"],
    ["hr", "心率"],
    ["personal", "自定义指标"],
  ],
  study: [
    ["task", "学习任务"],
    ["quiz", "学习测验"],
    ["project", "学习项目"],
    ["exam", "知识测验"],
  ],
};
const fields: Record<
  string,
  Array<[string, string, number, number, boolean?]>
> = {
  health_bp: [
    ["systolic", "收缩压（mmHg）", 60, 260],
    ["diastolic", "舒张压（mmHg）", 30, 160],
    ["heart_rate", "同步心率（次/分）", 30, 220, true],
  ],
  health_weight: [
    ["weight", "体重（kg）", 20, 300],
    ["height", "身高（cm）", 50, 250, true],
    ["body_fat_pct", "体脂率（%）", 0, 100, true],
    ["muscle_mass", "肌肉量（kg）", 0, 300, true],
  ],
  health_sugar: [["value", "血糖（mmol/L）", 1, 35]],
  health_hr: [["heart_rate", "心率（次/分）", 30, 220]],
  health_personal: [["value", "测量值", -1e9, 1e9]],
};
const choices: Record<string, [string, string, string[][]]> = {
  health_bp: [
    "period",
    "测量时段",
    [
      ["morning", "晨起"],
      ["afternoon", "午后"],
      ["evening", "晚间"],
      ["before_sleep", "睡前"],
    ],
  ],
  health_sugar: [
    "measurement_type",
    "测量类型",
    [
      ["fasting", "空腹"],
      ["before_meal", "餐前"],
      ["after_meal_1h", "餐后1小时"],
      ["after_meal_2h", "餐后2小时"],
      ["bedtime", "睡前"],
      ["random", "随机"],
    ],
  ],
  health_hr: [
    "state",
    "测量状态",
    [
      ["resting", "静息"],
      ["active", "活动中"],
      ["post_exercise", "运动后"],
    ],
  ],
};
export function phaseTwoPayload(kind: string, form: Record<string, any>) {
  if (kind === "water") return { water_cups: Number(form.water_cups) };
  if (kind === "review")
    return {
      progress_snapshot: Number(form.progress_snapshot),
      achievements: form.achievements,
      obstacles: form.obstacles,
      lessons: form.lessons,
      adjustments: form.adjustments,
    };
  const payload: Record<string, any> = {
    measured_at:
      form.measured_at === form._initialMeasuredAt && form._sourceMeasuredAt
        ? form._sourceMeasuredAt
        : new Date(form.measured_at).toISOString(),
    note: form.note,
  };
  for (const [key] of fields[kind] || [])
    payload[key] =
      form[key] == null || form[key] === "" ? null : Number(form[key]);
  if (choices[kind]) payload[choices[kind][0]] = form[choices[kind][0]];
  return payload;
}
export default function PhaseTwoEditor({
  kind,
  form,
  field,
}: {
  kind: string;
  form: Record<string, any>;
  field: (name: string, value: any) => void;
}) {
  if (kind === "water")
    return (
      <>
        <p className="rc-hint">{form.date} · 每杯按 250 ml 计算，按天累计。</p>
        <label>
          当天饮水（杯）
          <input
            required
            type="number"
            min={0}
            max={200}
            step={1}
            value={form.water_cups}
            onChange={(e) => field("water_cups", e.target.value)}
          />
        </label>
        <p className="rc-hint">设置为 0 会移除当天的饮水汇总，餐食数据保留。</p>
      </>
    );
  if (kind === "review")
    return (
      <>
        <p className="rc-hint">
          {form.goal_title} · 修改历史复盘不会覆盖当前目标进度。
        </p>
        <label>
          当时进度（%）
          <input
            required
            type="number"
            min={0}
            max={100}
            step={1}
            value={form.progress_snapshot}
            onChange={(e) => field("progress_snapshot", e.target.value)}
          />
        </label>
        {[
          ["achievements", "本次进展"],
          ["obstacles", "遇到的困难"],
          ["lessons", "收获与反思"],
          ["adjustments", "下一步调整"],
        ].map(([key, label]) => (
          <label key={key}>
            {label}
            <textarea
              maxLength={20000}
              rows={3}
              value={form[key] || ""}
              onChange={(e) => field(key, e.target.value)}
            />
          </label>
        ))}
      </>
    );
  const choice = choices[kind];
  return (
    <>
      <label>
        测量时间
        <input
          required
          type="datetime-local"
          step="any"
          value={form.measured_at}
          onChange={(e) => field("measured_at", e.target.value)}
        />
      </label>
      <div className="rc-form-grid">
        {(fields[kind] || []).map(([key, label, min, max, optional]) => (
          <label key={key}>
            {kind === "health_personal"
              ? `${form.metric_name || label}${form.unit ? `（${form.unit}）` : ""}`
              : label}
            <input
              type="number"
              step={
                ["systolic", "diastolic", "heart_rate"].includes(key)
                  ? "1"
                  : "any"
              }
              min={min}
              max={max}
              required={!optional}
              value={form[key] ?? ""}
              onChange={(e) => field(key, e.target.value)}
            />
          </label>
        ))}
        {choice && (
          <label>
            {choice[1]}
            <select
              value={form[choice[0]]}
              onChange={(e) => field(choice[0], e.target.value)}
            >
              {choice[2].map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label>
        测量备注
        <textarea
          rows={4}
          maxLength={20000}
          value={form.note || ""}
          onChange={(e) => field("note", e.target.value)}
        />
      </label>
    </>
  );
}
